import { useCallback, useEffect, useRef, useState } from 'react';
import { EMBED_TO_PARENT_MESSAGES, PARENT_TO_EMBED_MESSAGES } from 'src/constants/EmbedMessageConstants';
import { postMessageToEmbeddedIframe } from 'src/components/das-finsuite/embedded-apps/utils/EmbeddingUtils';
import type { SearchResultSection, SearchCategory, SearchVariant, ProxySearchResultActions } from '@amzn/das-finsuite-shared-ui';
import { logger } from 'src/logger';

/** All valid embed-to-parent message types for origin validation */
const VALID_EMBED_MESSAGE_TYPES = new Set(Object.values(EMBED_TO_PARENT_MESSAGES));

export interface EmbeddedSearchCapabilities {
  supported: boolean;
  placeholder: string;
  categories: SearchCategory[];
  variant: SearchVariant;
}

export interface UseEmbeddedSearchReturn {
  /** Non-null when embedded app supports search delegation */
  capabilities: EmbeddedSearchCapabilities | null;
  /** Search result sections from the embedded app */
  sections: SearchResultSection[];
  /** Send a search query to the embedded app */
  sendQuery: (query: string, category?: string) => void;
  /** Notify embedded app that user clicked a result */
  sendResultClick: (result: { path?: string; externalUrl?: string }) => void;
  /** Notify embedded app that user triggered an action */
  sendAction: (resultId: string, action: string) => void;
}

/**
 * Listens for search capabilities and results from an embedded iframe.
 * Returns proxy state that GlobalSearchBar uses to switch from local to delegated search.
 */
export const useEmbeddedSearch = (isEmbedActive: boolean): UseEmbeddedSearchReturn => {
  const [capabilities, setCapabilities] = useState<EmbeddedSearchCapabilities | null>(null);
  const [sections, setSections] = useState<SearchResultSection[]>([]);
  const currentQueryRef = useRef('');
  const isEmbedActiveRef = useRef(isEmbedActive);
  isEmbedActiveRef.current = isEmbedActive;

  // Reset when embed deactivates
  useEffect(() => {
    if (!isEmbedActive) {
      setCapabilities(null);
      setSections([]);
      currentQueryRef.current = '';
    }
  }, [isEmbedActive]);

  // Listen for messages from embedded app
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      const { type, payload } = event.data || {};

      // Validate message is a known embed-to-parent type
      if (!type || !VALID_EMBED_MESSAGE_TYPES.has(type)) return;

      // Verify the message originates from the embedded iframe
      const iframe = document.querySelector<HTMLIFrameElement>('iframe[data-embed-app]');
      if (!iframe || event.source !== iframe.contentWindow) return;

      // Guard against stale messages after embed deactivation
      if (!isEmbedActiveRef.current) return;

      logger.debug('useEmbeddedSearch: received message', { type });

      switch (type) {
        case EMBED_TO_PARENT_MESSAGES.SEARCH_CAPABILITIES: {
          if (payload?.supported) {
            setCapabilities({
              supported: true,
              placeholder: payload.placeholder || 'Search...',
              categories: payload.categories || [],
              variant: payload.variant || 'verity'
            });
            logger.info('useEmbeddedSearch: proxy mode activated', { variant: payload.variant });
          }
          break;
        }
        case EMBED_TO_PARENT_MESSAGES.SEARCH_RESULTS: {
          // Only accept results for the current query to avoid stale data
          if (payload?.query === currentQueryRef.current) {
            setSections(payload.sections || []);
          }
          break;
        }
        case EMBED_TO_PARENT_MESSAGES.SEARCH_ACTION_RESULT: {
          if (!payload) break;
          const { resultId, success, updatedFavoriteState } = payload;
          if (success && typeof updatedFavoriteState === 'boolean') {
            // Update the favorite state in the local sections copy
            setSections((prev) =>
              prev.map((s) => ({
                ...s,
                results: s.results.map((r) => {
                  if (r.id !== resultId) return r;
                  const actions = r.actions as ProxySearchResultActions | undefined;
                  return { ...r, actions: { ...actions, favorite: { isFavorited: updatedFavoriteState } } };
                })
              }))
            );
          } else if (!success) {
            logger.warn('useEmbeddedSearch: action failed for result', { resultId });
          }
          break;
        }
        case EMBED_TO_PARENT_MESSAGES.IFRAME_CLICK: {
          // Dispatch synthetic mousedown so click-outside handlers close dropdowns
          document.dispatchEvent(new MouseEvent('mousedown', { bubbles: true }));
          break;
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  const sendQuery = useCallback((query: string, category?: string) => {
    currentQueryRef.current = query;
    if (!query.trim()) {
      setSections([]);
      return;
    }
    postMessageToEmbeddedIframe(PARENT_TO_EMBED_MESSAGES.SEARCH_QUERY, { query, category });
  }, []);

  const sendResultClick = useCallback((result: { path?: string; externalUrl?: string }) => {
    postMessageToEmbeddedIframe(PARENT_TO_EMBED_MESSAGES.SEARCH_RESULT_CLICK, result as Record<string, unknown>);
  }, []);

  const sendAction = useCallback((resultId: string, action: string) => {
    postMessageToEmbeddedIframe(PARENT_TO_EMBED_MESSAGES.SEARCH_ACTION, { resultId, action });
  }, []);

  return { capabilities, sections, sendQuery, sendResultClick, sendAction };
};
